// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

/**
 * Stage 3 of the Power Flow model: coordinates.
 *
 * See docs/power-flow/power-flow-single-line-diagram.md §7.3 and §7.4.
 *
 * Two passes, in this order:
 *
 * 1. **Order within each rank** by the barycenter of a node's neighbours, a few
 *    sweeps down and up. Cheap, and enough to keep a UPS's four PDUs together
 *    underneath it rather than scattered across the rank.
 * 2. **Position**, deepest rank first. The bottom rank is packed left to right;
 *    every shallower node is then centred over its own children and overlaps
 *    are pushed right. That is what makes a UPS sit above the PDUs it feeds
 *    instead of above the middle of the drawing.
 *
 * **Every bus is a bar, at every depth.** Everything tapped off a switchboard
 * is at the same potential, and that is the single most useful thing a one-line
 * says, so a switchboard, a panelboard and an RPP are all drawn as a bar with
 * short vertical drops to what they feed.
 *
 * Generalising this was tried once before and reverted, because a bar spans
 * from its leftmost to its rightmost tap and therefore only reads as a bar when
 * those taps are clustered underneath it. What was missing then was
 * **contiguity**, and `orderKeyOf` now supplies it: the ordering pass below
 * treats a node's group key as the primary sort at every sweep, so a block's
 * assets can never be interleaved with another block's by a barycenter round.
 * With that guarantee a bar spans its own block and nothing else.
 *
 * **Chips are sized to their label** (`widthOf`). A fixed-width chip truncated
 * almost every name on a real site — `RPP-PDU-…`, `Power Distributi…` — and a
 * chip whose label is elided carries no information at all, which is the same
 * as not drawing it. Callers pass a measured width; the default keeps every
 * chip at `nodeWidth`, so a test that does not care about text is unaffected.
 */

import type { PowerFlowLoadGroup, PowerFlowDisplay } from './powerFlowCollapse';
import type { PowerFlowVertex } from './powerFlowGraph';

export interface PowerFlowLayoutOptions {
  /** Chip width, when no `widthOf` is supplied, and the floor for a measured one. */
  nodeWidth: number;
  nodeHeight: number;
  /** Ceiling on a measured chip width: past this, a long label truncates. */
  maxNodeWidth: number;
  /** Horizontal gap between adjacent chips in a rank. */
  nodeGapX: number;
  /**
   * Horizontal gap when either neighbour is a bus bar.
   *
   * Wider than `nodeGapX` on purpose. Two bars separated by a chip's gap read
   * as one continuous bus, which is the single worst misreading a one-line can
   * invite: it says the A and B sides are at the same potential when the whole
   * point of an A/B plant is that they are not.
   */
  busGapX: number;
  /** Vertical distance between rank baselines. */
  rankGapY: number;
  /** Thickness of a bus bar. */
  busHeight: number;
  /** Space above a bus bar reserved for its label. */
  busLabelHeight: number;
  /** Padding around the whole drawing. */
  padding: number;
  /** Barycenter sweeps. Two down/up rounds is the knee of the curve here. */
  orderingSweeps: number;
  /**
   * Total ordering tie-break. Natural-sorted so `PDU-A2` precedes `PDU-A10`;
   * the page passes the same label helper the chips render, so the order a
   * reader sees matches the order it was sorted by.
   */
  labelOf?: (_id: string) => string;
  /**
   * A sort key consulted before the label when ordering a rank. Used to keep a
   * block's assets together and a feeder group's blocks adjacent; a node with
   * no key sorts after every node with one.
   */
  orderKeyOf?: (_id: string) => string;
  /**
   * Measured width for a node, before clamping to
   * `[nodeWidth, maxNodeWidth]`. Text measurement needs a DOM, so it is the
   * page's job, not this module's.
   *
   * The second argument is what the node *is* — a group badge reads
   * `IT Rack × 12` and is measured from that, not from the asset label the
   * caller would otherwise reach for.
   */
  widthOf?: (_id: string, _node: PowerFlowNodeIdentity) => number;
  /**
   * Offset from the node's left edge to its conductor port, for a node whose
   * port is not its centre — a device drawn as its symbol. Undefined means the
   * centre. The placement pass centres a parent's *port* over its children's
   * ports, so the drop lands on the symbol whatever its text is doing.
   */
  portXOf?: (_id: string, _node: PowerFlowNodeIdentity) => number | undefined;
}

export const DEFAULT_LAYOUT_OPTIONS: PowerFlowLayoutOptions = {
  nodeWidth: 104,
  nodeHeight: 44,
  maxNodeWidth: 208,
  nodeGapX: 18,
  busGapX: 56,
  rankGapY: 116,
  busHeight: 10,
  busLabelHeight: 20,
  padding: 32,
  orderingSweeps: 2,
};

/**
 * Layout adjacency, derived from the display's acyclic remainder.
 *
 * Separate from `PowerFlowVertex.in`/`out`, which describe what the payload
 * published — ties included. Geometry must follow only the edges that survived
 * as downward conductors.
 */
interface TreeAdjacency {
  children: Map<string, string[]>;
  parents: Map<string, string[]>;
  rankOf: ReadonlyMap<string, number>;
}

export type PowerFlowShape = 'chip' | 'bus' | 'group';

/** What `widthOf` is told about the node it is measuring. */
export interface PowerFlowNodeIdentity {
  vertex: PowerFlowVertex;
  /** Present only for a collapsed load group. */
  group?: PowerFlowLoadGroup;
}

export interface PowerFlowNode {
  id: string;
  rank: number;
  shape: PowerFlowShape;
  x: number;
  y: number;
  w: number;
  h: number;
  /**
   * Stage x where conductors enter and leave. The box centre for a chip or a
   * group; the symbol's centre for a device drawn as its symbol, which sits at
   * the node's left with its text to the right. Absent means the centre.
   */
  port?: number;
  vertex: PowerFlowVertex;
  /** Set when `shape` is `group`: the loads this one node stands in for. */
  group?: PowerFlowLoadGroup;
}

/** Where a node's conductors attach, in stage x. */
export const portOf = (node: Pick<PowerFlowNode, 'x' | 'w' | 'port'>): number =>
  node.port ?? node.x + node.w / 2;

export interface PowerFlowRankRow {
  rank: number;
  /** Top of the rank's content, in stage coordinates. */
  y: number;
  height: number;
  /** True when this rank contains at least one bus bar. */
  hasBus: boolean;
}

export interface PowerFlowLayout {
  nodes: PowerFlowNode[];
  nodesById: Map<string, PowerFlowNode>;
  rows: PowerFlowRankRow[];
  width: number;
  height: number;
}

const naturalCompare = new Intl.Collator('en', { numeric: true, sensitivity: 'base' }).compare;

/**
 * Lay the display out.
 *
 * Deterministic: every sort is total, and the barycenter sweeps start from the
 * sorted order rather than from the input array's order.
 */
export const layoutPowerFlow = (
  display: PowerFlowDisplay,
  overrides: Partial<PowerFlowLayoutOptions> = {}
): PowerFlowLayout => {
  const options = { ...DEFAULT_LAYOUT_OPTIONS, ...overrides };
  const { verticesById, groupsById, ranks, rankOf, treeLinks } = display;

  if (ranks.length === 0) {
    return { nodes: [], nodesById: new Map(), rows: [], width: 0, height: 0 };
  }

  // Adjacency for layout comes from `treeLinks`, **not** from the vertices'
  // own `in`/`out`. Those still contain the tie edges the ranking pass removed,
  // and a tie must not influence geometry: a bar that spans toward the board it
  // is tied to reaches sideways across the drawing, and an ordering sweep that
  // follows a tie drags the two sides of the plant together.
  const children = new Map<string, string[]>();
  const parents = new Map<string, string[]>();
  for (const id of Array.from(verticesById.keys())) {
    children.set(id, []);
    parents.set(id, []);
  }
  for (const link of treeLinks) {
    children.get(link.source)?.push(link.target);
    parents.get(link.target)?.push(link.source);
  }
  for (const list of Array.from(children.values())) list.sort();
  for (const list of Array.from(parents.values())) list.sort();
  const tree: TreeAdjacency = { children, parents, rankOf };

  const labelOf = options.labelOf ?? ((id: string) => id);
  const orderKeyOf = options.orderKeyOf ?? (() => '');
  const compareIds = (a: string, b: string) =>
    naturalCompare(labelOf(a), labelOf(b)) || naturalCompare(a, b);

  // Every bus-role asset with something tapped off it is a bar, at whatever
  // depth it sits; the contiguity that makes this legible comes from
  // `orderKeyOf` — see the header. A bus with nothing drawn beneath it is a
  // box: a bar exists to show that its taps share a potential, and an RPP
  // whose racks have been folded away has no taps to show. The site's own
  // Ignition sheets draw exactly that — RPPs as boxes, boards as bars.
  const isBus = (id: string): boolean =>
    verticesById.get(id)?.role === 'bus' && (children.get(id) ?? []).length > 0;

  /** Clamped so neither an empty label nor a paragraph can distort the drawing. */
  const widthOf = (id: string): number => {
    const vertex = verticesById.get(id);
    if (!options.widthOf || !vertex) return options.nodeWidth;
    const group = groupsById.get(id);
    const measured = options.widthOf(id, group ? { vertex, group } : { vertex });
    if (!Number.isFinite(measured)) return options.nodeWidth;
    return Math.min(Math.max(measured, options.nodeWidth), options.maxNodeWidth);
  };

  /** Offset of a symbol's port from the node's left edge; undefined means the centre. */
  const symbolPortOffsetOf = (id: string): number | undefined => {
    const vertex = verticesById.get(id);
    if (!options.portXOf || !vertex || isBus(id)) return undefined;
    const group = groupsById.get(id);
    const offset = options.portXOf(id, group ? { vertex, group } : { vertex });
    return offset !== undefined && Number.isFinite(offset)
      ? Math.min(Math.max(offset, 0), widthOf(id))
      : undefined;
  };
  const portOffsetOf = (id: string): number => symbolPortOffsetOf(id) ?? widthOf(id) / 2;

  const order = orderRanks(ranks, tree, options.orderingSweeps, compareIds, orderKeyOf);
  const xOf = assignX(order, tree, options, isBus, widthOf, portOffsetOf);

  // --- Vertical placement ------------------------------------------------
  // A rank can hold chips and bars at once, so a row is measured from what is
  // actually in it rather than assumed uniform.
  const rows: PowerFlowRankRow[] = [];
  let cursorY = options.padding;
  const rankGap = options.rankGapY - options.nodeHeight;
  for (let rank = 0; rank < order.length; rank += 1) {
    const hasBus = order[rank].some(isBus);
    const hasChip = order[rank].some(id => !isBus(id));
    const contentHeight = Math.max(
      hasChip ? options.nodeHeight : 0,
      hasBus ? options.busLabelHeight + options.busHeight : 0
    );
    rows.push({ rank, y: cursorY, height: contentHeight, hasBus });
    cursorY += contentHeight + rankGap;
  }

  const nodes: PowerFlowNode[] = [];
  for (let rank = 0; rank < order.length; rank += 1) {
    const row = rows[rank];
    for (const id of order[rank]) {
      const vertex = verticesById.get(id);
      if (!vertex) continue;
      const span = xOf.get(id);
      if (!span) continue;
      const group = groupsById.get(id);
      const bus = isBus(id);
      const symbolPort = symbolPortOffsetOf(id);
      nodes.push({
        id,
        rank,
        shape: bus ? 'bus' : group ? 'group' : 'chip',
        x: span.x,
        // A bar sits at the row's baseline, so it is as close as it can be to
        // what hangs off it and its exit y matches a chip's in the same row.
        y: bus ? row.y + row.height - options.busHeight : row.y,
        w: span.w,
        h: bus ? options.busHeight : row.height,
        ...(symbolPort === undefined ? {} : { port: span.x + symbolPort }),
        vertex,
        ...(group ? { group } : {}),
      });
    }
  }

  // --- Normalise so the drawing starts at the padding --------------------
  const minX = nodes.reduce((min, node) => Math.min(min, node.x), Number.POSITIVE_INFINITY);
  const shift = Number.isFinite(minX) ? options.padding - minX : 0;
  for (const node of nodes) {
    node.x += shift;
    if (node.port !== undefined) node.port += shift;
  }

  const width = nodes.reduce((max, node) => Math.max(max, node.x + node.w), 0) + options.padding;
  const height = nodes.reduce((max, node) => Math.max(max, node.y + node.h), 0) + options.padding;

  return {
    nodes,
    nodesById: new Map(nodes.map(node => [node.id, node])),
    rows,
    width,
    height,
  };
};

/**
 * Order each rank so connected nodes end up near each other.
 *
 * Barycenter: a node's key is the mean position of its neighbours in the
 * adjacent rank. Sweeping downward orders each rank by its parents, upward by
 * its children; alternating a couple of times settles a shape like
 * `bus → UPS → PDU` into blocks. `compareIds` is the total tie-break, so a rank
 * whose nodes all share a barycenter (the first rank, every leaf under one
 * parent) still comes out in a stable, human order.
 */
const orderRanks = (
  ranks: readonly string[][],
  tree: TreeAdjacency,
  sweeps: number,
  compareIds: (_a: string, _b: string) => number,
  orderKeyOf: (_id: string) => string
): string[][] => {
  /**
   * Group key first, always.
   *
   * This is what makes a bus bar legible at every depth. A barycenter sweep on
   * its own will happily interleave block A's PDUs with block B's when their
   * parents sit near each other, and a bar drawn across an interleaved rank
   * spans the other block's assets too — which asserts they are at the same
   * potential. Sorting on the key before the barycenter confines every sweep to
   * reordering *within* a block.
   */
  const byKey = (a: string, b: string) => naturalCompare(orderKeyOf(a), orderKeyOf(b));

  const order = ranks.map(rank => rank.slice().sort((a, b) => byKey(a, b) || compareIds(a, b)));

  const positionsIn = (rank: number): Map<string, number> => {
    const positions = new Map<string, number>();
    (order[rank] ?? []).forEach((id, index) => positions.set(id, index));
    return positions;
  };

  const sweep = (rank: number, neighbourRank: number, side: 'parents' | 'children') => {
    const positions = positionsIn(neighbourRank);
    const keys = new Map<string, number>();
    for (const id of order[rank]) {
      const neighbours = (tree[side].get(id) ?? []).filter(other => positions.has(other));
      // No neighbour in that rank → keep the node where it is, so a sweep never
      // flings an unconnected node to position zero.
      keys.set(
        id,
        neighbours.length === 0
          ? order[rank].indexOf(id)
          : neighbours.reduce((sum, other) => sum + (positions.get(other) ?? 0), 0) /
              neighbours.length
      );
    }
    order[rank] = order[rank]
      .slice()
      .sort((a, b) => byKey(a, b) || (keys.get(a) ?? 0) - (keys.get(b) ?? 0) || compareIds(a, b));
  };

  for (let round = 0; round < sweeps; round += 1) {
    for (let rank = 1; rank < order.length; rank += 1) sweep(rank, rank - 1, 'parents');
    for (let rank = order.length - 2; rank >= 0; rank -= 1) sweep(rank, rank + 1, 'children');
  }

  return order;
};

interface Span {
  x: number;
  w: number;
}

/**
 * Horizontal positions, deepest rank first.
 *
 * The bottom rank is the ruler: it is packed left to right in its ordered
 * sequence. Every shallower node then wants to sit centred over its own
 * children, and a left-to-right pass enforces the minimum gap — so a node is
 * centred on its subtree unless a sibling to its left has already claimed the
 * space, which is the classic tidy-tree compromise and reads correctly on a
 * one-line.
 *
 * A bus is not a point: it spans from its leftmost to its rightmost child, so
 * the taps down to them are short verticals rather than long diagonals.
 */
const assignX = (
  order: readonly string[][],
  tree: TreeAdjacency,
  options: PowerFlowLayoutOptions,
  isBus: (_id: string) => boolean,
  widthOf: (_id: string) => number,
  portOffsetOf: (_id: string) => number
): Map<string, Span> => {
  const spans = new Map<string, Span>();

  /** The gap that has to separate two horizontally adjacent nodes. */
  const gapBetween = (left: string, right: string | undefined): number =>
    isBus(left) || (right !== undefined && isBus(right)) ? options.busGapX : options.nodeGapX;

  // The deepest rank has nothing below it to centre on, so it is simply packed.
  const deepest = order.length - 1;
  let packCursor = 0;
  order[deepest].forEach((id, index) => {
    const w = widthOf(id);
    spans.set(id, { x: packCursor, w });
    packCursor += w + gapBetween(id, order[deepest][index + 1]);
  });

  for (let rank = deepest - 1; rank >= 0; rank -= 1) {
    // What each node would like: centred over its placed children, or — with
    // nothing below it — wherever the left-to-right pass puts it. `null` means
    // "no preference", which packs it against its left neighbour instead of
    // leaving a gap the size of a chip that has nothing in it.
    const desired = order[rank].map(id => {
      const bus = isBus(id);
      const allChildren = tree.children.get(id) ?? [];

      // **Immediate children only** for the geometry that follows. A node whose
      // subtree reaches several ranks down — the diamond where a switchboard
      // feeds a PDU directly as well as through a UPS — would otherwise be
      // centred on, or in a bus's case stretched across, assets two rows away.
      // The bar has to span the row it actually taps into.
      const nextRank = allChildren.filter(child => tree.rankOf.get(child) === rank + 1);
      const relevant = (nextRank.length > 0 ? nextRank : allChildren)
        .map(child => spans.get(child))
        .filter((span): span is Span => span !== undefined);

      if (relevant.length === 0) return { id, x: null, w: widthOf(id), isBus: bus };

      const left = relevant.reduce((min, span) => Math.min(min, span.x), Number.POSITIVE_INFINITY);
      const right = relevant.reduce((max, span) => Math.max(max, span.x + span.w), 0);

      if (bus) {
        // Span the row it feeds, but never narrower than a chip — a switchboard
        // feeding one UPS still has to be legible and carry its label.
        return { id, x: left, w: Math.max(right - left, widthOf(id)), isBus: true };
      }

      // Centre the node's *port* over its children's ports: a symbol node's
      // drop must land on the symbol, not on the middle of its text. A bus
      // child's port is the middle of the bar it was just spanned to, which is
      // usually far wider than `portOffsetOf` (its minimum label width) knows.
      const ports = (nextRank.length > 0 ? nextRank : allChildren)
        .map(child => {
          const span = spans.get(child);
          if (span === undefined) return undefined;
          return span.x + (isBus(child) ? span.w / 2 : portOffsetOf(child));
        })
        .filter((port): port is number => port !== undefined);
      const w = widthOf(id);
      return {
        id,
        x: (Math.min(...ports) + Math.max(...ports)) / 2 - portOffsetOf(id),
        w,
        isBus: false,
      };
    });

    // Left-to-right overlap resolution. `desired` is already in rank order, so
    // this preserves the barycenter ordering while guaranteeing the gap.
    let cursor = Number.NEGATIVE_INFINITY;
    for (let index = 0; index < desired.length; index += 1) {
      const entry = desired[index];
      const base = entry.x ?? (Number.isFinite(cursor) ? cursor : 0);
      const x = Math.max(base, cursor);
      spans.set(entry.id, { x, w: entry.w });
      cursor = x + entry.w + gapBetween(entry.id, desired[index + 1]?.id);
    }
  }

  return spans;
};
